import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  Check,
  MessageSquare,
  ShieldCheck,
} from "lucide-react";
import { appSignupUrl } from "../config";
import { useMountEffect } from "../hooks/useMountEffect";
import { usePageMeta } from "../hooks/usePageMeta";
import BookDemoModal from "../components/BookDemoModal";
import DuskNav from "../components/dusk/DuskNav";
import DuskFooter from "../components/dusk/DuskFooter";
import EventWorkspace from "../components/dusk/EventWorkspace";
import "../styles/dusk.css";
import "../styles/dusk-experience.css";

const Arrow: React.FC = () => <ArrowRight size={16} aria-hidden="true" />;

const STEPS = [
  {
    id: "brief",
    title: "Tell us what you're planning.",
    body: "Tell Comtev what you have in mind. It uses your brief to search for venues, suppliers, flights and rooms.",
  },
  {
    id: "quotes",
    title: "Stop chasing replies.",
    body: "Comtev contacts suppliers, chases replies and negotiates rates. You get a shortlist with prices and terms to review.",
  },
  {
    id: "budget",
    title: "See what each option costs.",
    body: "Keep quotes and travel estimates in one budget. Save different versions and share a proposal for review.",
  },
  {
    id: "signoff",
    title: "Keep the final say.",
    body: "Send spending to a named approver. Flights and hotels are only booked after approval. The approval history stays with the event.",
  },
];

const PERSONAS = [
  {
    slug: "event-leads",
    role: "Event leads",
    line: "Get supplier replies and quotes without chasing each email.",
  },
  {
    slug: "heads-of-events",
    role: "Heads of events",
    line: "See every event's threads, budget and approvals in one place.",
  },
  {
    slug: "operations",
    role: "Operations teams",
    line: "Plan the company kickoff alongside the rest of your work.",
  },
  {
    slug: "assistants",
    role: "Executive assistants",
    line: "Send costs for approval and keep track of who has replied.",
  },
];

/* Mirrors the FAQPage JSON-LD in public/index.html. Keep the two in step. */
const FAQS = [
  {
    q: "What does Comtev do?",
    a: "Comtev helps corporate event teams find suppliers, negotiate quotes, estimate travel costs and collect guest details. Budgets and approvals stay in the same workspace. It is built in the UK and is free to start, with paid plans from £79 a month.",
  },
  {
    q: "Does Comtev source quotes in languages other than English?",
    a: "Yes. Comtev drafts vendor outreach in the vendor's own language, including German, French, Spanish, Italian, Japanese and Arabic. A German caterer receives German and a Tokyo AV vendor receives Japanese, from the same brief.",
  },
  {
    q: "How does Comtev collect the guest list?",
    a: "Add collaborators to your event and Comtev asks each of them for guest details over Slack and WhatsApp. Contributors need no app. Comtev chases whoever is slow to reply and holds duplicates for review.",
  },
  {
    q: "Do I need to download a separate app?",
    a: "No. Comtev works on web and mobile, and the AI planning tools are built into the platform for every user. Collaborators contributing a guest list need no app at all.",
  },
  {
    q: "How does Comtev handle check-ins?",
    a: "Comtev issues digital passes with QR codes to guests. On the day, door staff scan the codes for check-in, and a live dashboard shows attendance as it happens.",
  },
];

const HomePageDusk: React.FC = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState<string | null>(null);
  usePageMeta({
    title: "AI Corporate Event Planning Software | Comtev",
    description:
      "AI corporate event planning software that sources suppliers, negotiates quotes and builds your budget. Explore Comtev, from the first brief to the last arrival.",
    canonicalPath: "/",
  });

  /* index.html pre-paints the atmospheric hero via html.home-dusk on a hard
     load. Mirror it for in-app navigation, and hand the page back to the
     marketing background on the way out. */
  useMountEffect(() => {
    window.scrollTo({ top: 0 });
    const root = document.documentElement;
    root.classList.add("home-dusk");
    return () => root.classList.remove("home-dusk");
  });

  const openDemo = (source: string) => {
    setDemoSource(source);
    setDemoOpen(true);
  };

  return (
    <div className="dusk dusk-experience">
      <DuskNav onBookDemo={() => openDemo("dusk-nav")} />
      <main id="main">
        <section className="event-intro" aria-labelledby="event-intro-title">
          <div className="event-atmosphere" aria-hidden="true" />
          <div className="event-hero-copy">
            <p className="event-eyebrow">
              <span /> AI FOR CORPORATE EVENT TEAMS
            </p>
            <h1 id="event-intro-title">
              Great events.
              <br />
              <span>Less legwork.</span>
            </h1>
            <p className="event-hero-body">
              Comtev finds suppliers, chases quotes and brings your budget
              together.
              <br className="event-desktop-break" /> You choose what gets
              booked.
            </p>
            <div className="event-hero-actions">
              <a className="dusk-btn" href={appSignupUrl()} data-cta="hero-sign-up">
                Sign up <Arrow />
              </a>
              <button
                className="dusk-btn dusk-btn-ghost"
                type="button"
                data-cta="book-demo"
                onClick={() => openDemo("dusk-hero")}
              >
                Book a demo
              </button>
              <a className="event-tour-link" href="#workspace">
                Explore an event <ArrowDown size={15} aria-hidden="true" />
              </a>
            </div>
            <p className="event-hero-note">
              Free to start. Plans from £79 a month. No supplier commission.
            </p>
          </div>
          <div className="event-demo-wrap" id="workspace">
            <div className="event-demo-caption">
              <span>
                <span className="event-signal" /> A COMPANY KICKOFF IN BARCELONA
              </span>
              <span>
                Interactive sample
                <span className="event-sample-detail">
                  {" "}
                  · Barcelona, 140 people
                </span>
              </span>
            </div>
            <EventWorkspace />
          </div>
          <div className="event-principles">
            <span>
              <MessageSquare size={16} aria-hidden="true" /> Supplier follow-ups
              handled.
            </span>
            <span>
              <Check size={16} aria-hidden="true" /> Quotes and estimates in one
              budget.
            </span>
            <span>
              <ShieldCheck size={16} aria-hidden="true" /> Bookings need your
              approval.
            </span>
          </div>
        </section>

        {/* ══════════ THE SAME STORY, WRITTEN DOWN ══════════ */}
        <section className="dusk-wrap dusk-block event-how" id="how">
          <p className="event-section-label">HOW COMTEV WORKS</p>
          <h2 className="dusk-h2">
            Less time in the inbox. More time on the event.
          </h2>
          <ol className="dusk-ledger">
            {STEPS.map((s, i) => (
              <li key={s.id}>
                <span className="dusk-step-n">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="dusk-wrap dusk-block" id="price">
          <div className="dusk-price">
            <h2 className="dusk-h2">
              Free to start. No commission from venues or suppliers.
            </h2>
            <p className="dusk-body">
              Open a free account and plan your first event. Paid plans from
              £79 a month add more AI credits, more events and a bigger team.
              Every plan has the planning tools shown here, and Comtev does not
              take a cut from suppliers.
            </p>
            <Link className="dusk-link" to="/pricing">
              See pricing
              <Arrow />
            </Link>
          </div>
        </section>

        <section className="dusk-wrap dusk-block dusk-two" id="who">
          <div>
            <h2 className="dusk-h2">
              Built for the person the event lands on.
            </h2>
          </div>
          <ol className="dusk-rows">
            {PERSONAS.map((p, i) => (
              <li key={p.slug}>
                <Link to={`/for/${p.slug}`} className="dusk-row">
                  <span className="dusk-row-main">
                    <span className="dusk-row-t">{p.role}</span>
                    <span className="dusk-row-d">{p.line}</span>
                  </span>
                  <span className="dusk-step-n">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </section>

        <section className="dusk-wrap dusk-block dusk-two" id="faq">
          <div>
            <h2 className="dusk-h2">What people ask before a demo.</h2>
            <p className="dusk-body">
              Something else?{" "}
              <Link className="dusk-link" to="/contact">
                Ask us
                <Arrow />
              </Link>
            </p>
          </div>
          <div className="dusk-faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="dusk-wrap dusk-block" id="demo">
          <div className="dusk-close">
            <h2 className="dusk-h2">Bring a real event to the demo.</h2>
            <p className="dusk-body">
              Bring your brief, even if it is still rough. We will show you how
              to find suppliers, work out costs and get the event approved.
            </p>
            <div className="dusk-close-actions">
              <a className="dusk-btn" href={appSignupUrl()} data-cta="close-sign-up">
                Sign up
                <Arrow />
              </a>
              <button
                type="button"
                className="dusk-btn dusk-btn-ghost"
                data-cta="book-demo"
                onClick={() => openDemo("dusk-close")}
              >
                Book a demo
              </button>
            </div>
          </div>
        </section>

        <DuskFooter />
      </main>

      <BookDemoModal
        isOpen={demoOpen}
        source={demoSource}
        onClose={() => {
          setDemoOpen(false);
        }}
      />
    </div>
  );
};

export default HomePageDusk;
